import React from 'react';
import cx from 'classnames';
import { parseInt } from 'lodash';
import { FormControl, Select, MenuItem } from '@mui/material';
import MuiSelectUpDown from './MuiSelectUpDown';
import SkeletonWrapper from './skeleton/SkeletonWrapper';
import { SelectorTitle } from './SelectorTitle';

/**
 * @typedef {object} CustomSelectorOwnProps
 * @property {React.ReactNode} [title]
 * @property {boolean} [showAsterisk]
 * @property {string} [placeholder]
 * @property {Array<*>} [options]
 * @property {(selectedValue: *) => void} [setValue]
 * @property {number|string} [value]
 * @property {boolean} [loading]
 * @property {string} [testId]
 * @property {Object} [menuProps]
 */

/**
 * @typedef {CustomSelectorOwnProps & React.ComponentProps<typeof Select>} CustomSelectorProps
 */

/**
 * @param {CustomSelectorProps} props
 */
const CustomSelector = props => {
  const {
    title = '',
    showAsterisk = true,
    placeholder = '',
    options = [],
    value = '',
    setValue,
    disabled,
    loading = false,
    testId = '',
    menuProps = {},
    ...otherProps
  } = props;

  const filteredOptions = options.filter(x => !x.disabled);
  const selectedIndex = filteredOptions.findIndex(x => x.value === value);
  const disableUpArrow = selectedIndex !== -1 && selectedIndex === 0;
  const disableDownArrow = selectedIndex !== -1 && selectedIndex === filteredOptions.length - 1;

  const classes = ['mui-input-number__choice-adornment', 'mui-input-number__hover-pointer'];
  const wrapperClasses = ['mui-input-number mui-input-number__choice-wrapper'];
  const Adornment = (
    <MuiSelectUpDown
      className={cx(classes)}
      disableDownArrow={disabled || disableDownArrow}
      disableUpArrow={disabled || disableUpArrow}
      onClickDown={() =>
        onValueChange(selectedIndex !== -1 ? selectedIndex + 1 : filteredOptions.length - 1)
      }
      onClickUp={() => onValueChange(selectedIndex !== -1 ? selectedIndex - 1 : 0)}
      upDownId={testId}
    />
  );

  const onValueChange = value => {
    const newValue = parseInt(value);
    setValue(filteredOptions[newValue].value);
  };

  return (
    <>
      <SelectorTitle title={title} testId={testId} showAsterisk={showAsterisk} />
      <SkeletonWrapper show={loading}>
        <div className={cx(wrapperClasses)} data-testid={testId}>
          <FormControl variant='standard'>
            <Select
              variant='standard'
              {...otherProps}
              disabled={disabled}
              value={options.some(option => option.value === value) ? value : ''}
              onChange={e => setValue(e.target.value)}
              renderValue={
                options.some(option => option.value === value)
                  ? undefined
                  : () => <p>{placeholder}</p>
              }
              displayEmpty
              inputProps={{
                'data-testid': testId + '-input'
              }}
              IconComponent={() => null}
              disableUnderline
              SelectDisplayProps={{
                'data-testid': testId + '-selectDisplay'
              }}
              MenuProps={{
                ...menuProps,
                'data-testid': testId + '-options'
              }}>
              {options.map((option, i) => (
                <MenuItem value={option.value} key={i} disabled={option.disabled}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {Adornment}
        </div>
      </SkeletonWrapper>
    </>
  );
};

export default CustomSelector;
